/**
 * WidgetRegistry — Constellation-driven widget resolution (DRR-319)
 *
 * Maps widget IDs from constellation YAML to React components.
 * Subgraph packages register their widgets; SubjectDashboard renders via lookup.
 *
 * Core widgets live here. Subgraph widgets register on package import.
 */

import React from 'react';
import { Alert, Skeleton, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

export interface WidgetProps {
  tenantNum: number;
  subjectActorId: string;
  config?: Record<string, unknown>;
}

export type WidgetComponent = React.FC<WidgetProps>;

interface WidgetDefinition {
  component: WidgetComponent;
  provider: 'core' | 'fhir' | 'erp' | 'legal';
  description: string;
}

const registry = new Map<string, WidgetDefinition>();

export function registerWidget(
  widgetId: string,
  component: WidgetComponent,
  provider: WidgetDefinition['provider'],
  description: string
): void {
  if (registry.has(widgetId)) {
    console.warn(`Widget "${widgetId}" already registered, overwriting`);
  }
  registry.set(widgetId, { component, provider, description });
}

export function getWidget(widgetId: string): WidgetDefinition | undefined {
  return registry.get(widgetId);
}

export function listWidgets(): Array<{ id: string } & WidgetDefinition> {
  return Array.from(registry.entries()).map(([id, def]) => ({ id, ...def }));
}

export interface WidgetSlotProps {
  widgetId: string;
  tenantNum: number;
  subjectActorId: string;
  config?: Record<string, unknown>;
  fallback?: React.ReactNode;
}

export function WidgetSlot({
  widgetId,
  tenantNum,
  subjectActorId,
  config,
  fallback
}: WidgetSlotProps): React.ReactElement {
  const definition = registry.get(widgetId);

  if (!definition) {
    if (fallback) return <>{fallback}</>;
    return (
      <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
        Widget "{widgetId}" not registered
      </Alert>
    );
  }

  const Component = definition.component;
  return <Component tenantNum={tenantNum} subjectActorId={subjectActorId} config={config} />;
}

export function WidgetSlotSkeleton(): React.ReactElement {
  return (
    <Stack gap="xs">
      <Skeleton height={20} width="60%" />
      <Skeleton height={40} />
      <Skeleton height={40} />
    </Stack>
  );
}
